import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { Mail, MessageSquare, HelpCircle, Bug, Lightbulb, DollarSign, Send } from 'lucide-react'
import Button from '../components/common/Button'

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'general',
    subject: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  const contactReasons = [
    {
      icon: HelpCircle,
      title: 'General Support',
      description: 'Questions about using HireFlow',
      email: 'hireflowapp@gmail.com',
      value: 'general',
    },
    {
      icon: Bug,
      title: 'Bug Reports',
      description: 'Report technical issues',
      email: 'hireflowapp@gmail.com',
      value: 'bug',
    },
    {
      icon: Lightbulb,
      title: 'Feature Requests',
      description: 'Suggest new features',
      email: 'hireflowapp@gmail.com',
      value: 'feature',
    },
    {
      icon: DollarSign,
      title: 'Billing & Refunds',
      description: 'Payment and refund inquiries',
      email: 'hireflowapp@gmail.com',
      value: 'billing',
    },
    {
      icon: MessageSquare,
      title: 'Partnerships',
      description: 'Business opportunities',
      email: 'hireflowapp@gmail.com',
      value: 'partnership',
    },
    {
      icon: Mail,
      title: 'Other',
      description: 'Everything else',
      email: 'hireflowapp@gmail.com',
      value: 'other',
    },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      // Simulate API call - Replace with actual endpoint
      await new Promise(resolve => setTimeout(resolve, 1500))

      setSubmitStatus('success')
      setFormData({
        name: '',
        email: '',
        category: 'general',
        subject: '',
        message: '',
      })
    } catch (error) {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  return (
    <>
      <Helmet>
        <title>Contact Us - HireFlow</title>
        <meta
          name="description"
          content="Get in touch with the HireFlow team. We're here to help with support, feedback, billing, and partnership inquiries."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-primary-50 via-white to-primary-50">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="heading-xl mb-6">Get in Touch</h1>
            <p className="text-xl text-neutral-600">
              Have a question, feedback, or need help? We're here for you.
              Choose the best way to reach us below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Reasons */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="heading-md mb-4">How Can We Help?</h2>
              <p className="text-lg text-neutral-600">
                Select the category that best matches your inquiry
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {contactReasons.map((reason, index) => (
                <motion.div
                  key={reason.value}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="card p-6 h-full hover:border-primary-200 transition-colors">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                      <reason.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-neutral-600 mb-3">
                      {reason.description}
                    </p>
                    <a
                      href={`mailto:${reason.email}`}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {reason.email}
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Form */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <div className="card p-8 max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-neutral-600">
                    Fill out the form below and we'll get back to you within 24-48 hours
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="john@example.com"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-neutral-700 mb-2">
                      Category
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    >
                      {contactReasons.map((reason) => (
                        <option key={reason.value} value={reason.value}>
                          {reason.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-neutral-700 mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Brief description of your inquiry"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us more about your inquiry..."
                    />
                  </div>

                  {/* Submit Status */}
                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm">
                        Thank you for your message! We'll get back to you within 24-48 hours.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm">
                        Something went wrong. Please try again or email us directly at hireflowapp@gmail.com
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="section-padding bg-neutral-50">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="heading-md mb-6">Before You Contact Us</h2>
            <p className="text-lg text-neutral-600 mb-8">
              Check out these resources - you might find your answer faster!
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="card p-6">
                <h3 className="font-semibold text-neutral-900 mb-2">Pricing FAQ</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  Questions about plans and billing
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  href="/pricing"
                  fullWidth
                >
                  View Pricing
                </Button>
              </div>
              <div className="card p-6">
                <h3 className="font-semibold text-neutral-900 mb-2">Refund Policy</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  14-day money-back guarantee
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  href="/refund"
                  fullWidth
                >
                  Learn More
                </Button>
              </div>
              <div className="card p-6">
                <h3 className="font-semibold text-neutral-900 mb-2">Privacy & Terms</h3>
                <p className="text-sm text-neutral-600 mb-4">
                  How we protect your data
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  href="/privacy"
                  fullWidth
                >
                  Read Policy
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default Contact
